import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { BadgeVariant, badgeVariantToClassName } from 'src/utils/badgeUtils';

export type ChipSelectOption = {
    id: string;
    label: string;
    disabled?: boolean;
    badgeVariant?: BadgeVariant;
    badgeLabel?: ReactNode;
    badgeShortLabel?: ReactNode;
    badgeShortLabels?: Partial<Record<BadgeVariant, ReactNode>>;
    badgeIndicators?: BadgeVariant[];
    options?: ChipSelectOption[];
};

type FlatChipSelectOption = ChipSelectOption & {
    depth: number;
};

export type ChipSelectProps = {
    label?: ReactNode;
    ariaLabel?: string;
    options: ChipSelectOption[];
    selectedIds: string[];
    displayedSelectedIds?: string[];
    onChange: (nextIds: string[]) => void;
    disabled?: boolean;
    placeholder?: ReactNode;
    className?: string;
    closeOnSelect?: boolean;
};

export const ChipSelect = ({
    label,
    ariaLabel,
    options,
    selectedIds,
    displayedSelectedIds,
    onChange,
    disabled = false,
    placeholder = 'Select',
    className = '',
    closeOnSelect = false,
}: ChipSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const displayedSet = useMemo(
        () => new Set(displayedSelectedIds ?? selectedIds),
        [displayedSelectedIds, selectedIds],
    );
    const flatOptions = useMemo(() => {
        const flatten = (
            list: ChipSelectOption[],
            depth = 0,
        ): FlatChipSelectOption[] =>
            list.flatMap(option => [
                { ...option, depth },
                ...flatten(option.options ?? [], depth + 1),
            ]);

        return flatten(options);
    }, [options]);
    const selectedOptions = useMemo(
        () => flatOptions.filter(option => displayedSet.has(option.id)),
        [displayedSet, flatOptions],
    );
    const availableOptions = useMemo(
        () => flatOptions.filter(option => !selectedSet.has(option.id)),
        [flatOptions, selectedSet],
    );
    const canOpen = !disabled && availableOptions.length > 0;

    const toggle = (id: string, optionDisabled?: boolean) => {
        if (disabled || optionDisabled) return;
        const next = new Set(selectedSet);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        onChange(Array.from(next));
        if (closeOnSelect) setIsOpen(false);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div
            ref={containerRef}
            className={cx('flex flex-col gap-2', className)}
        >
            {label ? (
                <div className="text-sm font-medium text-woodyBrown">
                    {label}
                </div>
            ) : null}
            <div className="relative">
                <button
                    type="button"
                    aria-label={ariaLabel}
                    onClick={() => {
                        if (!canOpen) return;
                        setIsOpen(prev => !prev);
                    }}
                    disabled={disabled}
                    className="min-h-[40px] w-full rounded-xl border px-3 py-2 text-left text-sm flex items-center bg-woodyBrown text-white"
                >
                    <div className="flex flex-wrap gap-2 flex-1">
                        {selectedOptions.length ? (
                            selectedOptions.map(option => (
                                <span
                                    key={option.id}
                                    role="button"
                                    onClick={event => {
                                        event.stopPropagation();
                                        toggle(option.id, option.disabled);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs bg-starkWhite/10 text-lightPeach border-starkWhite/30"
                                >
                                    {option.label}
                                    {(
                                        option.badgeIndicators ??
                                        (option.badgeVariant
                                            ? [option.badgeVariant]
                                            : [])
                                    ).map(variant => {
                                        const shortLabel =
                                            option.badgeShortLabels?.[
                                                variant
                                            ] ?? option.badgeShortLabel;

                                        return shortLabel ? (
                                            <span
                                                key={variant}
                                                className={cx(
                                                    'inline-flex items-center justify-center h-4 min-w-[16px] rounded-full text-[10px] leading-none font-semibold',
                                                    badgeVariantToClassName[
                                                        variant
                                                    ],
                                                )}
                                            >
                                                {shortLabel}
                                            </span>
                                        ) : null;
                                    })}
                                    <span className="text-lightPeach/70 hover:text-lightPeach">
                                        ✕
                                    </span>
                                </span>
                            ))
                        ) : (
                            <span className="text-lightPeach">
                                {placeholder}
                            </span>
                        )}
                    </div>
                    {canOpen ? (
                        <span
                            className={cx(
                                'ml-auto text-lightPeach transition-transform',
                                isOpen ? 'rotate-180' : '',
                            )}
                            aria-hidden="true"
                        >
                            ▾
                        </span>
                    ) : null}
                </button>
                {isOpen && canOpen && (
                    <div className="absolute z-50 mt-2 w-full rounded-xl border border-woodyBrown/20 bg-white shadow-lg max-h-56 overflow-auto">
                        <div className="flex flex-col">
                            {availableOptions.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() =>
                                        toggle(option.id, option.disabled)
                                    }
                                    disabled={disabled || option.disabled}
                                    className={cx(
                                        'flex items-center gap-3 pr-3 py-2 text-sm transition-colors text-woodyBrown hover:bg-woodyBrown/5',
                                        option.depth > 0 ? 'pl-6' : 'pl-3',
                                        disabled || option.disabled
                                            ? 'opacity-60 cursor-not-allowed'
                                            : '',
                                    )}
                                >
                                    <span className="flex items-center gap-2">
                                        {option.label}
                                    </span>
                                    {option.badgeLabel ? (
                                        <span
                                            className={cx(
                                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                                option.badgeVariant &&
                                                    badgeVariantToClassName[
                                                        option.badgeVariant
                                                    ],
                                            )}
                                        >
                                            {option.badgeLabel}
                                        </span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
